import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { createStructuredSelector } from 'reselect';
import { FormattedMessage, intlShape, injectIntl } from 'react-intl';
import { fromJS } from 'immutable';
import validator from 'validator';
import { uniqueId } from 'lodash';
import ReactResizeDetector from 'react-resize-detector';
import * as _ from 'lodash';
import moment from 'moment';
import injectReducer from 'utils/injectReducer';
import { removeTimeZoneFromDate } from 'utils/dateUtil';
import { makeSelectMedicalExpensesTugoStep, makeSelectMedicalExpensesTugoStepErrors } from './selectors';
import reducer from './reducer';
import messages from './messages';

import PageSectionHeader from '../components/PageSectionHeader';
import FlexBox from '../../../components/FlexBox';
import {
  ValidateGroup,
} from '../../../components/Validation';
import {
  handleFormDataChange,
  nextSlide,
  resetFormFields,
  resetFormMedicalExpenses,
} from './actions';
import DatePicker from '../components/DatePicker/index';
import Textbox from '../components/Textbox';
import TextArea from '../components/TextArea';
import OptionsContainer from '../components/OptionsContainer';
import { CLAIMING_FOR } from './constants';
import { saveFormData } from '../actions';
import { makeSelectCurrentStep } from '../../Wizard/selectors';
import { makeSelectProgressBarStatus } from '../../ProgressBar/selectors';
import TimePickerInput from '../../../components/TimePickerInput';
import CurrencySelector from '../components/CurrencySelector';
import {
  ErrorMessage,
  Validate,
} from '../../../components/Validation/';
import { required, checkDateOnly } from '../../../utils/validation/validator';
import { stepMedicalAndEvacuationLookup } from '../../FastCover/dataMapper/claimCodeMapper';
import FNOLTooltip from '../../../components/FNOLTooltip';
import { AdditionalList } from './components/Additional';
import { makeSelectInsuredsList, makeSelectIsClaimForFieldEnabled, makeSelectPolicyTypeCode, makeSelectFormHasChanges } from '../../App/selectors';
import { setFormHasChanges } from '../../App/actions';
import WizardNavigationButtons from '../components/WizardNavigationButtons';

export class MedicalExpensesTugoStep extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      validationCounter: 0,
      errors: [],
    };

    // Define reset mappings for hospital form fields
    this.hospitalFormResets = {
      didYouContactUsPriorToYourHospitalization: {
        yes: [
          'isYourClaimRelatedToASelfInflictedInjuryOrSuicideAttempt',
          'infoInPatientHospitalExpensesSelfInflictedInjuriesAndAttemptedSuicideItems',
          'infoHospitalChargesRelatedToPsychiatricTreatmentItems',
          'infoHospitalChargesRelatedToHIVItems',
          'infoHospitalizationChargesItems',
          'isYourClaimRelatedToPsychiatricTreatment',
          'isYourClaimRelatedToHIV'
        ],
        no: [
          'isYourClaimRelatedToASelfInflictedInjuryOrSuicideAttempt',
          'infoInPatientHospitalExpensesSelfInflictedInjuriesAndAttemptedSuicideItems',
          'infoHospitalChargesRelatedToPsychiatricTreatmentItems',
          'infoHospitalChargesRelatedToHIVItems',
          'infoHospitalizationChargesItems',
          'isYourClaimRelatedToPsychiatricTreatment',
          'isYourClaimRelatedToHIV'
        ]
      },
      selfInflicted: {
        yes: [
          'infoHospitalChargesRelatedToPsychiatricTreatmentItems',
          'infoHospitalChargesRelatedToHIVItems',
          'infoHospitalizationChargesItems',
          'isYourClaimRelatedToPsychiatricTreatment',
          'isYourClaimRelatedToHIV'
        ],
        no: [
          'infoInPatientHospitalExpensesSelfInflictedInjuriesAndAttemptedSuicideItems',
          'infoHospitalChargesRelatedToPsychiatricTreatmentItems',
          'infoHospitalChargesRelatedToHIVItems',
          'infoHospitalizationChargesItems',
          'isYourClaimRelatedToPsychiatricTreatment',
          'isYourClaimRelatedToHIV'
        ]
      },
      psychiatric: {
        yes: [
          'infoHospitalChargesRelatedToHIVItems',
          'infoHospitalizationChargesItems',
          'isYourClaimRelatedToHIV'
        ],
        no: [
          'infoHospitalChargesRelatedToPsychiatricTreatmentItems',
          'infoHospitalChargesRelatedToHIVItems',
          'infoHospitalizationChargesItems',
          'isYourClaimRelatedToHIV'
        ]
      },
      hiv: {
        yes: ['infoHospitalizationChargesItems'],
        no: ['infoHospitalChargesRelatedToHIVItems']
      }
    };

    this.validatorGroup = new ValidateGroup();
    this.handleNext = this.handleNext.bind(this);
    this.checkFormError = this.checkFormError.bind(this);
    this.onResize = this.onResize.bind(this);

    this.saveButtonId = uniqueId();
    this.clickedItemId = '';
    this.nextButton = null;
  }

  componentWillReceiveProps(nextProps) {
    const { currentStep, step, stepName } = this.props;
    if ((stepName && nextProps.validateStep === stepName) || (currentStep !== nextProps.currentStep && currentStep === step)) {
      this.checkFormError();
    }

    if (this.props.isReadOnly && nextProps.errors !== this.props.errors) {
      this.state.validationCounter += 1;
      this.validatorGroup.checkValidation();
    }

    if (!nextProps.loading) {
      this.clickedItemId = '';
    }
  }

  onChange = (propertyName) => (event) => {
    if (event === null) {
      this.props.onFormDataChange(propertyName, null);
      this.props.setFormHasChanges(true);
      return;
    }

    if (event.constructor.name === 'Moment') {
      this.props.onFormDataChange(propertyName, removeTimeZoneFromDate(event.toDate().toString()));
    } else {
      const target = event.target;
      const value = target.type === 'checkbox' ? target.checked : target.value;
      if (target.type === 'radio') {
        this.props.adjust();
      }
      this.props.onFormDataChange(propertyName, value);
    }
    this.props.setFormHasChanges(true);
    // this.props.makeDirty(6);
  };

  onResize = () => {
    const { adjust } = this.props;
    if (adjust) {
      adjust();
    }
  };

  // Generic handler for all expense type changes
  handleExpenseTypeChange = (formField) => (data) => {
    this.props.onFormDataChange(`form.${formField}`, fromJS(data));
    this.props.setFormHasChanges(true);
    const { adjust } = this.props;
    if (adjust) adjust();
  };

  getSelectedClaiming = () => {
    const {
      claimForPassportCreditCard,
      claimForTheftOfCash,
      claimForLuggageAndPersonalEffect,
    } = this.props.form;

    let selectedValue = null;

    switch ('true') {
      case claimForPassportCreditCard:
        selectedValue = CLAIMING_FOR.PASSPORT_CREDIT_CARD;
        break;
      case claimForTheftOfCash:
        selectedValue = CLAIMING_FOR.THEFT_OF_CASH;
        break;
      case claimForLuggageAndPersonalEffect:
        selectedValue = CLAIMING_FOR.LUGGAGE_AND_PERSONAL_EFFECTS;
        break;
      default:
        selectedValue = null;
        break;
    }

    return selectedValue;
  };

  shouldDisable = (field) => {
    const selectedValue = this.getSelectedClaiming();

    return !!selectedValue && selectedValue !== field;
  };

  appendIds = (array) =>
    array.map((item) => {
      let newItem = item; // eslint-disable-line
      if (!item.id) {
        newItem.id = new Date().getTime().toString();
      }
      return newItem;
    });

  validateTableField(groups) {
    const invalid = _.some(groups, (fields) =>
      _.some(fields, (value) => {
        const newValue = value ? value.toString() : '';
        return validator.isEmpty(newValue);
      })
    );
    return invalid;
  }

  handleKeyDown = (e) => {
    if (e.shiftKey && e.key === 'Tab') {
      return;
    }
    if (e.key === 'Tab') {
      e.preventDefault();
    }
  };

  handleSaveButtonClick = (event) => {
    const target = event.currentTarget || event.srcElement;
    this.clickedItemId = target.id;
    this.handleFormDataSave();
    this.nextButton.focus();
  };

  showSpinnerForElement = (elementId) => this.clickedItemId === elementId;

  checkFormError() {
    const valid = this.validatorGroup.checkValidation();
    this.props.onNext(!valid);
    if (!valid) {
      this.props.makeError(this.props.step);
      const counter = this.state.validationCounter + 1;
      this.setState({ validationCounter: counter });
    } else {
      this.props.makeValid(this.props.step);
    }
  }

  handleChange = (propertyName) => (event) => {
    const target = event.target;
    const value = target.type === 'checkbox' ? target.checked : target.value;

    this.props.onFormDataChange(propertyName, value);
    this.props.setFormHasChanges(true);
  };

  handleChangeAmount = (propertyName) => (event) => {
    const target = event.target;
    const regex = /^\d*(\.\d{0,2})?$/;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    if (regex.test(value)) {
      this.props.onFormDataChange(propertyName, value);
      this.props.setFormHasChanges(true);
    }
  };

  handleChangePhoneNumber = (propertyName) => (event) => {
    const target = event.target;
    const regex = /^[0-9-]+$/;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    if (regex.test(value) || value === '') {
      this.props.onFormDataChange(propertyName, value);
      this.props.setFormHasChanges(true);
    }
  };

  handleNext = () => {
    const { submitted } = this.props;
    if (submitted) {
      this.props.next();
      return;
    }

    // const errors = this.validatorGroup.getErrors();
    const valid = this.validatorGroup.checkValidation();

    this.props.onNext(!valid);
    if (!valid) {
      this.props.makeError(this.props.step);
      const counter = this.state.validationCounter + 1;
      this.setState({ validationCounter: counter });
    } else {
      this.props.makeValid(this.props.step);
    }

    this.handleFormDataSave();
    this.props.next();
  };

  handleFormDataSave = () => {
    this.props.onFormDataSave();
    this.props.setFormHasChanges(false);
  };

  handleDayChange = (propertyName) => (day) => {
    const dayWithoutTimeZone = removeTimeZoneFromDate(day);
    this.props.onFormDataChange(propertyName, dayWithoutTimeZone);
    this.props.setFormHasChanges(true);
    // this.props.makeDirty(6);
  };

  handleChangeClaimingFor = (propertyName) => (event) => {
    const target = event.target;
    const value = target.name;
    this.props.onFormDataChange(propertyName, value);
    this.props.setFormHasChanges(true);
  };

  summaryFormValue() {
    return _.mapValues(this.props.form, (prop) => prop || 'N/A');
  }

  handleUncheckedFormState = (field, resetAction) => (event) => {
    const target = event.target;
    const value = target.type === 'checkbox' ? target.checked : target.value;

    if (value === 'false') {
      resetAction();
    }

    this.handleChange(field)(event);
  };

  handleCurrencyChange = (propertyName) => (value) => {
    this.props.onFormDataChange(propertyName, value);
    this.props.setFormHasChanges(true);
  }

  handleExpensesSelected = (propertyName) => (event) => {
    const { whatExpensesAreYouClaimingFor, diagnosticImaging } = this.props.form;
    // Determine which list to use based on propertyName
    const sourceList = propertyName === 'form.diagnosticImaging' ? diagnosticImaging : whatExpensesAreYouClaimingFor;
    const expensesSelected = !!sourceList && typeof sourceList.toJS === 'function'
      ? sourceList.toJS()
      : sourceList || [];
    const listChecked = _.cloneDeep(expensesSelected);

    const target = event.target;
    const name = target.name;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    if (value) {
      listChecked.push(name);
    } else {
      listChecked.splice(listChecked.indexOf(name), 1);
      if (name === 'Outpatient Doctor or Clinic Visit') {
        this.props.onResetFormCustom(['infoOutpatientDoctorOrClinicVisitItems', 'infoOutpatientDoctorOrClinicVisitSelfInflictedInjuriesAndAttemptedSuicideItems', 'isClaimRelatedToSelfInflictedNo', 'isClaimRelatedToSelfInflictedYes']);
      }
      if (name === 'Diagnostic imaging') {
        this.props.onResetFormCustom(['infodDiagnosticNonMRIItems', 'infodDiagnosticMRIItems', 'diagnosticImaging']);
      }
      if (name === 'In Patient Hospital Expenses') {
        this.props.onResetFormCustom(
          [
            'didYouContactUsPriorToYourHospitalization',
            'isYourClaimRelatedToASelfInflictedInjuryOrSuicideAttempt',
            'infoInPatientHospitalExpensesSelfInflictedInjuriesAndAttemptedSuicideItems',
            'infoHospitalChargesRelatedToPsychiatricTreatmentItems',
            'infoHospitalChargesRelatedToHIVItems',
            'infoHospitalizationChargesItems',
            'isYourClaimRelatedToPsychiatricTreatment',
            'isYourClaimRelatedToHIV'
          ]);
      }
      if (name === 'Well Baby Care') {
        this.props.onResetFormCustom(['infoWellBabyCareItems', 'babysDateOfBirth']);
      }
      if (name === 'Annual Health Exam') {
        this.props.onResetFormCustom(['infoAnnualHealthExamItems']);
      }
      if (name === 'Blood plasma or whole blood (transfusion)') {
        this.props.onResetFormCustom(['infoBloodPlasmaItems']);
      }
      if (name === 'Anaesthesiology') {
        this.props.onResetFormCustom(['infoAnaesthesiologyItems']);
      }
      if (name === 'Dental Surgery') {
        this.props.onResetFormCustom(['infoDentalSurgeryItems']);
      }
      if (name === 'Vaccination Expenses') {
        this.props.onResetFormCustom(['infoVaccinationExpensesItems']);
      }
      if (name === 'Cleft Lip and Palate Assistance') {
        this.props.onResetFormCustom(['infoCleftLipAndPalateAssistanceItems']);
      }
      if (name === 'Maternity Expense') {
        this.props.onResetFormCustom(['infoMaternityExpenseItems']);
      }
      if (name === 'Oncology') {
        this.props.onResetFormCustom(['infoOncologyItems']);
      }
    }
    this.props.onFormDataChange(propertyName, listChecked);
    this.props.setFormHasChanges(true);
  };

  // Generic render method for all expense type items
  renderExpensesItems(formField) {
    const items = this.props.form[formField];
    if (!items) {
      return null;
    }

    const newMedicalExpenseItems = this.appendIds(items);
    const { isSummary, submitted, insuredsList, isClaimForFieldEnabled } = this.props;
    const isReadOnly = submitted || isSummary;

    return (
      <div>
        <AdditionalList
          data={newMedicalExpenseItems}
          onChange={this.handleExpenseTypeChange(formField)}
          isDelayed={false}
          disabled={isReadOnly}
          validatorGroup={this.validatorGroup}
          validationCounter={this.state.validationCounter}
          insuredsList={insuredsList}
          isClaimForFieldEnabled={isClaimForFieldEnabled}
        />
      </div>
    );
  }

  renderDiagnosticExpenses() {
    const {
      diagnosticImaging = [],
    } = this.props.isSummary ? this.summaryFormValue() : this.props.form;
    const { isSummary, submitted } = this.props;
    const isReadOnly = submitted || isSummary;
    return (
      <div>
        <div className={'row'}>
          <div className={'pt-2 col-sm-12 form-group'}>
            <input
              disabled={isReadOnly}
              type="checkbox"
              name="diagnosticNonMRI"
              checked={diagnosticImaging.includes('diagnosticNonMRI')}
              onChange={this.handleExpensesSelected('form.diagnosticImaging')}
            />
            <span>
              <FormattedMessage {...messages.diagnosticNonMRI} />
            </span>
            {diagnosticImaging.includes('diagnosticNonMRI') && (
              <div className={'ml-2 row'}>
                <div className={'col-sm-12 form-group'}>
                  <b><FormattedMessage {...messages.standardXRayAndLaboratoryExaminations} /></b>
                  {this.renderExpensesItems('infodDiagnosticNonMRIItems')}
                </div>
              </div>
            )}

          </div>

          <div className={'pt-2 col-sm-12 form-group'}>
            <input
              disabled={isReadOnly}
              type="checkbox"
              name="diagnosticMRI"
              checked={diagnosticImaging.includes('diagnosticMRI')}
              onChange={this.handleExpensesSelected('form.diagnosticImaging')}
            />
            <span>
              <FormattedMessage {...messages.diagnosticMRI} />
            </span>
            {diagnosticImaging.includes('diagnosticMRI') && (
              <div className={'ml-2 row'}>
                <div className={'col-sm-12 form-group'}>
                  <b><FormattedMessage {...messages.mriScans} /></b>
                  {this.renderExpensesItems('infodDiagnosticMRIItems')}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  renderOutpatientDoctorOrClinicVisit() {
    const {
      isClaimRelatedToSelfInflictedYes,
      isClaimRelatedToSelfInflictedNo,
    } = this.props.isSummary ? this.summaryFormValue() : this.props.form;

    const { isSummary, submitted, policyTypeCode } = this.props;
    const isReadOnly = submitted || isSummary;
    return (
      <div>
        <PageSectionHeader className='pt-2'>
          <FormattedMessage {...messages.isClaimRelatedToSelfInflicted} />
        </PageSectionHeader>
        <div className={'row'}>
          <div className={'pt-2 col-sm-12 form-group'}>
            <FlexBox>
              <OptionsContainer>
                <input
                  disabled={isReadOnly}
                  type="radio"
                  name="isClaimRelatedToSelfInflicted"
                  value="isClaimRelatedToSelfInflictedYes"
                  checked={isClaimRelatedToSelfInflictedYes === 'true'}
                  onChange={(e) => {
                    this.handleChange('form.isClaimRelatedToSelfInflictedYes')({ target: { value: 'true' } });
                    this.handleChange('form.isClaimRelatedToSelfInflictedNo')({ target: { value: 'false' } });
                    this.props.onResetFormCustom(['infoOutpatientDoctorOrClinicVisitItems']); // Reset the other form
                  }}
                />
                <FormattedMessage {...messages.yes} />
              </OptionsContainer>
              <OptionsContainer>
                <input
                  disabled={isReadOnly}
                  type="radio"
                  name="diagnosticType"
                  value="isClaimRelatedToSelfInflictedNo"
                  checked={isClaimRelatedToSelfInflictedNo === 'true'}
                  onChange={(e) => {
                    this.handleChange('form.isClaimRelatedToSelfInflictedNo')({ target: { value: 'true' } });
                    this.handleChange('form.isClaimRelatedToSelfInflictedYes')({ target: { value: 'false' } });
                    this.props.onResetFormCustom(['infoOutpatientDoctorOrClinicVisitSelfInflictedInjuriesAndAttemptedSuicideItems']); // Reset the other form
                  }}
                />
                <FormattedMessage {...messages.no} />
              </OptionsContainer>
            </FlexBox>
          </div>
        </div>
        {(isClaimRelatedToSelfInflictedYes === 'true') &&
          <div className={'ml-2 row'}>
            <div className={'col-sm-12 form-group'}>
              <b><FormattedMessage {...messages.selfInflictedInjuriesAndAttemptedSuicide} /></b>
              {this.renderExpensesItems('infoOutpatientDoctorOrClinicVisitSelfInflictedInjuriesAndAttemptedSuicideItems')}
            </div>
          </div>
        }
        {(isClaimRelatedToSelfInflictedNo === 'true') &&
          <div className={'ml-2 row'}>
            <div className={'col-sm-12 form-group'}>
              <b><FormattedMessage {...messages.outpatientDoctorOrClinicVisit} /></b>
              {this.renderExpensesItems('infoOutpatientDoctorOrClinicVisitItems')}
            </div>
          </div>
        }
      </div>
    );
  }

  renderInPatientHospitalExpenses() {
    const {
      didYouContactUsPriorToYourHospitalization,
      isYourClaimRelatedToASelfInflictedInjuryOrSuicideAttempt,
      isYourClaimRelatedToPsychiatricTreatment,
      isYourClaimRelatedToHIV,
    } = this.props.isSummary ? this.summaryFormValue() : this.props.form;
    const { isSummary, submitted } = this.props;
    const isReadOnly = submitted || isSummary;

    return (
      <div>
        <div className={'ml-4 pt-2'}>
          <div className={'col-sm-12 form-group'}>
            <label>
              <FormattedMessage {...messages.didYouContactUsPriorToYourHospitalization} />
            </label>
            <div>
              <input
                type="radio"
                name="didYouContactUsPriorToYourHospitalization"
                value="yes"
                checked={didYouContactUsPriorToYourHospitalization === 'yes'}
                onChange={((e) => {
                  this.handleChange('form.didYouContactUsPriorToYourHospitalization')(e);
                  this.props.onResetFormCustom(this.hospitalFormResets.didYouContactUsPriorToYourHospitalization.yes);
                })}
                disabled={isReadOnly}
              />
              <span className="ml-2">
                <FormattedMessage {...messages.yes} />
              </span>
              <input
                className="ml-3"
                type="radio"
                name="didYouContactUsPriorToYourHospitalization"
                value="no"
                checked={didYouContactUsPriorToYourHospitalization === 'no'}
                onChange={((e) => {
                  this.handleChange('form.didYouContactUsPriorToYourHospitalization')(e);
                  this.props.onResetFormCustom(this.hospitalFormResets.didYouContactUsPriorToYourHospitalization.no);
                })}
                disabled={isReadOnly}
              />
              <span className="ml-2">
                <FormattedMessage {...messages.no} />
              </span>
            </div>
          </div>
          {didYouContactUsPriorToYourHospitalization === 'no' && (
            <div className={'ml-4 alert alert-warning'}>
              <i className="fa fa-info-circle tooltip-icon mr-2"></i>
              <FormattedMessage {...messages.hospitalizationWarning} />
            </div>
          )}

          {(didYouContactUsPriorToYourHospitalization === 'no' || didYouContactUsPriorToYourHospitalization === 'yes') && (
            <div className={'col-sm-12 form-group mt-3'}>
              <label>
                <FormattedMessage {...messages.isClaimRelatedToSelfInflicted} />
              </label>
              <div>
                <input
                  type="radio"
                  name="isYourClaimRelatedToASelfInflictedInjuryOrSuicideAttempt"
                  value="yes"
                  checked={isYourClaimRelatedToASelfInflictedInjuryOrSuicideAttempt === 'yes'}
                  onChange={((e) => {
                    this.handleChange('form.isYourClaimRelatedToASelfInflictedInjuryOrSuicideAttempt')(e);
                    this.props.onResetFormCustom(this.hospitalFormResets.selfInflicted.yes);
                  })}
                  disabled={isReadOnly}
                />
                <span className="ml-2">
                  <FormattedMessage {...messages.yes} />
                </span>
                <input
                  className="ml-3"
                  type="radio"
                  name="isYourClaimRelatedToASelfInflictedInjuryOrSuicideAttempt"
                  value="no"
                  checked={isYourClaimRelatedToASelfInflictedInjuryOrSuicideAttempt === 'no'}
                  onChange={((e) => {
                    this.handleChange('form.isYourClaimRelatedToASelfInflictedInjuryOrSuicideAttempt')(e);
                    this.props.onResetFormCustom(this.hospitalFormResets.selfInflicted.no);
                  })}
                  disabled={isReadOnly}
                />
                <span className="ml-2">
                  <FormattedMessage {...messages.no} />
                </span>
              </div>
            </div>
          )}

          {isYourClaimRelatedToASelfInflictedInjuryOrSuicideAttempt === 'yes' && (
            <div className={'col-sm-12 form-group'}>
              <b><FormattedMessage {...messages.selfInflictedInjuriesAndAttemptedSuicide} /></b>
              {this.renderExpensesItems('infoInPatientHospitalExpensesSelfInflictedInjuriesAndAttemptedSuicideItems')}
            </div>
          )}

          {(isYourClaimRelatedToASelfInflictedInjuryOrSuicideAttempt === 'no') && (
            <div className={'col-sm-12 form-group ml-2'}>
              <label>
                <FormattedMessage {...messages.isYourClaimRelatedToPsychiatricTreatment} />
              </label>
              <div>
                <input
                  type="radio"
                  name="isYourClaimRelatedToPsychiatricTreatment"
                  value="yes"
                  checked={isYourClaimRelatedToPsychiatricTreatment === 'yes'}
                  onChange={((e) => {
                    this.handleChange('form.isYourClaimRelatedToPsychiatricTreatment')(e);
                    this.props.onResetFormCustom(this.hospitalFormResets.psychiatric.yes);
                  }
                  )}
                  disabled={isReadOnly}
                />
                <span className="ml-2">
                  <FormattedMessage {...messages.yes} />
                </span>
                <input
                  className="ml-3"
                  type="radio"
                  name="isYourClaimRelatedToPsychiatricTreatment"
                  value="no"
                  checked={isYourClaimRelatedToPsychiatricTreatment === 'no'}
                  onChange={((e) => {
                    this.handleChange('form.isYourClaimRelatedToPsychiatricTreatment')(e);
                    this.props.onResetFormCustom(this.hospitalFormResets.psychiatric.no);
                  })}
                  disabled={isReadOnly}
                />
                <span className="ml-2">
                  <FormattedMessage {...messages.no} />
                </span>
              </div>
            </div>
          )}

          {isYourClaimRelatedToPsychiatricTreatment === 'yes' && (
            <div className={'col-sm-12 form-group'}>
              <b><FormattedMessage {...messages.hospitalChargesRelatedToPsychiatricTreatment} /></b>
              {this.renderExpensesItems('infoHospitalChargesRelatedToPsychiatricTreatmentItems')}
            </div>
          )}

          {isYourClaimRelatedToPsychiatricTreatment === 'no' && (
            <div className={'col-sm-12 form-group ml-2'}>
              <label>
                <FormattedMessage {...messages.isYourClaimRelatedToHIV} />
              </label>
              <div>
                <input
                  type="radio"
                  name="isYourClaimRelatedToHIV"
                  value="yes"
                  checked={isYourClaimRelatedToHIV === 'yes'}
                  onChange={((e) => {
                    this.handleChange('form.isYourClaimRelatedToHIV')(e);
                    this.props.onResetFormCustom(this.hospitalFormResets.hiv.yes);
                  }
                  )}
                  disabled={isReadOnly}
                />
                <span className="ml-2">
                  <FormattedMessage {...messages.yes} />
                </span>
                <input
                  className="ml-3"
                  type="radio"
                  name="isYourClaimRelatedToHIV"
                  value="no"
                  checked={isYourClaimRelatedToHIV === 'no'}
                  onChange={((e) => {
                    this.handleChange('form.isYourClaimRelatedToHIV')(e);
                    this.props.onResetFormCustom(this.hospitalFormResets.hiv.no);
                  }
                  )}
                  disabled={isReadOnly}
                />
                <span className="ml-2">
                  <FormattedMessage {...messages.no} />
                </span>
              </div>
            </div>
          )}

          {isYourClaimRelatedToHIV === 'yes' && (
            <div className={'col-sm-12 form-group'}>
              <b><FormattedMessage {...messages.hospitalChargesRelatedToHIV} /></b>
              {this.renderExpensesItems('infoHospitalChargesRelatedToHIVItems')}
            </div>
          )}

          {isYourClaimRelatedToHIV === 'no' && (
            <div className={'col-sm-12 form-group'}>
              <b><FormattedMessage {...messages.hospitalizationCharges} /></b>
              {this.renderExpensesItems('infoHospitalizationChargesItems')}
            </div>
          )}

        </div>
      </div>
    )
  }
  render() {
    const {
      whatExpensesAreYouClaimingFor,
      babysDateOfBirth
    } = isSummary ? this.summaryFormValue() : this.props.form;
    const { isSummary, submitted, loading, intl } = this.props;
    const isReadOnly = submitted || isSummary;
    const expensesSelected = !!whatExpensesAreYouClaimingFor && typeof whatExpensesAreYouClaimingFor.toJS === 'function'
      ? whatExpensesAreYouClaimingFor.toJS()
      : whatExpensesAreYouClaimingFor || [];

    return (
      <div className="app-content">
        <div>
          <div className={'col-sm-12 form-group'}>
            <PageSectionHeader>
              <FormattedMessage {...messages.thisSectionRelatedToMedicalExpenses} />
            </PageSectionHeader>
          </div>
          <div className={'col-sm-12 form-group'}>
            <Validate
              name="whatExpensesAreYouClaimingFor"
              validators={[required]}
              group={this.validatorGroup}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              defaultValue={expensesSelected}
              fieldName={messages.whatExpensesAreYouClaimingFor}
            >
              <div className={'pt-2 col-sm-12 form-group'}>
                <input
                  disabled={isReadOnly}
                  type="checkbox"
                  name="Outpatient Doctor or Clinic Visit"
                  checked={expensesSelected.includes('Outpatient Doctor or Clinic Visit')}
                  onChange={this.handleExpensesSelected('form.whatExpensesAreYouClaimingFor')}
                />
                <span>
                  <FormattedMessage {...messages.outpatientDoctorOrClinicVisit} />
                </span>
                {expensesSelected.includes('Outpatient Doctor or Clinic Visit') && (
                  <span>
                    <div className={'col-sm-12 form-group'}>
                      {this.renderOutpatientDoctorOrClinicVisit()}
                    </div>
                  </span>
                )}
              </div>

              <div className={'col-sm-12 form-group'}>
                <input
                  disabled={isReadOnly}
                  type="checkbox"
                  name="Diagnostic imaging"
                  checked={expensesSelected.includes('Diagnostic imaging')}
                  onChange={this.handleExpensesSelected('form.whatExpensesAreYouClaimingFor')}
                />
                <span>
                  <FormattedMessage {...messages.diagnosticImaging} />
                </span>
                {expensesSelected.includes('Diagnostic imaging') && (
                  <div className={'ml-1 row'}>
                    <div className="pt-2 col-sm-12 form-group">
                      {this.renderDiagnosticExpenses()}
                    </div>
                  </div>
                )}
              </div>

              <div className={'col-sm-12 form-group'}>
                <input
                  disabled={isReadOnly}
                  type="checkbox"
                  name="In Patient Hospital Expenses"
                  checked={expensesSelected.includes('In Patient Hospital Expenses')}
                  onChange={this.handleExpensesSelected('form.whatExpensesAreYouClaimingFor')}
                />
                <span>
                  <FormattedMessage {...messages.inPatientHospitalExpenses} />
                </span>
                {expensesSelected.includes('In Patient Hospital Expenses') && (
                  <div>
                    {this.renderInPatientHospitalExpenses()}
                  </div>
                )}
              </div>

              <div className={'col-sm-12 form-group'}>
                <input
                  disabled={isReadOnly}
                  type="checkbox"
                  name="Blood plasma or whole blood (transfusion)"
                  checked={expensesSelected.includes('Blood plasma or whole blood (transfusion)')}
                  onChange={this.handleExpensesSelected('form.whatExpensesAreYouClaimingFor')}
                />
                <span>
                  <FormattedMessage {...messages.bloodPlasmaOrWholeBloodTransfusion} />
                </span>
                {expensesSelected.includes('Blood plasma or whole blood (transfusion)') && (
                  <div>
                    {this.renderExpensesItems('infoBloodPlasmaItems')}
                  </div>
                )}
              </div>

              <div className={'col-sm-12 form-group'}>
                <input
                  disabled={isReadOnly}
                  type="checkbox"
                  name="Anaesthesiology"
                  checked={expensesSelected.includes('Anaesthesiology')}
                  onChange={this.handleExpensesSelected('form.whatExpensesAreYouClaimingFor')}
                />
                <span>
                  <FormattedMessage {...messages.anaesthesiology} />
                </span>
                {expensesSelected.includes('Anaesthesiology') && (
                  <div>
                    {this.renderExpensesItems('infoAnaesthesiologyItems')}
                  </div>
                )}
              </div>

              <div className={'col-sm-12 form-group'}>
                <input
                  disabled={isReadOnly}
                  type="checkbox"
                  name="Dental Surgery"
                  checked={expensesSelected.includes('Dental Surgery')}
                  onChange={this.handleExpensesSelected('form.whatExpensesAreYouClaimingFor')}
                />
                <span>
                  <FormattedMessage {...messages.dentalSurgery} />
                </span>
                {expensesSelected.includes('Dental Surgery') && (
                  <div>
                    {this.renderExpensesItems('infoDentalSurgeryItems')}
                  </div>
                )}
              </div>

              <div className={'col-sm-12 form-group'}>
                <input
                  disabled={isReadOnly}
                  type="checkbox"
                  name="Well Baby Care"
                  checked={expensesSelected.includes('Well Baby Care')}
                  onChange={this.handleExpensesSelected('form.whatExpensesAreYouClaimingFor')}
                />
                <span>
                  <FormattedMessage {...messages.wellBabyCare} />
                </span>
                {expensesSelected.includes('Well Baby Care') && (
                  <div>
                    <div className={'ml-2 col-sm-4 form-group pt-1'}>
                      <PageSectionHeader>
                        <FormattedMessage {...messages.babysDateOfBirth} />
                      </PageSectionHeader>
                      <DatePicker
                        position={'bottom-start'}
                        disabled={isReadOnly}
                        name="babysDateOfBirth"
                        type="text"
                        onDayChange={this.handleDayChange('form.babysDateOfBirth')}
                        value={babysDateOfBirth}
                      />
                    </div>
                    <div className={'ml-2 col-sm-12 form-group'}>
                      {this.renderExpensesItems('infoWellBabyCareItems')}
                    </div>
                  </div>
                )}
              </div>

              <div className={'col-sm-12 form-group'}>
                <input
                  disabled={isReadOnly}
                  type="checkbox"
                  name="Annual Health Exam"
                  checked={expensesSelected.includes('Annual Health Exam')}
                  onChange={this.handleExpensesSelected('form.whatExpensesAreYouClaimingFor')}
                />
                <span>
                  <FormattedMessage {...messages.annualHealthExam} />
                </span>
                {expensesSelected.includes('Annual Health Exam') && (
                  <span>
                    <div className={'col-sm-12 form-group'}>
                      {this.renderExpensesItems('infoAnnualHealthExamItems')}
                    </div>
                  </span>
                )}
              </div>

              <div className={'col-sm-12 form-group'}>
                <input
                  disabled={isReadOnly}
                  type="checkbox"
                  name="Vaccination Expenses"
                  checked={expensesSelected.includes('Vaccination Expenses')}
                  onChange={this.handleExpensesSelected('form.whatExpensesAreYouClaimingFor')}
                />
                <span>
                  <FormattedMessage {...messages.vaccinationExpenses} />
                </span>
                {expensesSelected.includes('Vaccination Expenses') && (
                  <span>
                    <div className={'col-sm-12 form-group'}>
                      {this.renderExpensesItems('infoVaccinationExpensesItems')}
                    </div>
                  </span>
                )}
              </div>

              <div className={'col-sm-12 form-group'}>
                <input
                  disabled={isReadOnly}
                  type="checkbox"
                  name="Cleft Lip and Palate Assistance"
                  checked={expensesSelected.includes('Cleft Lip and Palate Assistance')}
                  onChange={this.handleExpensesSelected('form.whatExpensesAreYouClaimingFor')}
                />
                <span>
                  <FormattedMessage {...messages.cleftLipAndPalateAssistance} />
                </span>
                {expensesSelected.includes('Cleft Lip and Palate Assistance') && (
                  <div>
                    <PageSectionHeader className='pt-2 ml-4'>
                      <FormattedMessage {...messages.cleftLipAndPalateAssistanceDescription} />
                    </PageSectionHeader>
                    <div className={'ml-4 pt-2 row'}>
                      <div className={'col-sm-12 form-group'}>
                        <b className='pt-2'>
                          <FormattedMessage {...messages.cleftLipAndPalateTreatment} />
                          <FNOLTooltip dataFor={'maternityExpense'} content={messages.cleftLipAndPalateTreatmentTooltip.defaultMessage} />
                        </b>
                        {this.renderExpensesItems('infoCleftLipAndPalateAssistanceItems')}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className={'col-sm-12 form-group'}>
                <input
                  disabled={isReadOnly}
                  type="checkbox"
                  name="Maternity Expense"
                  checked={expensesSelected.includes('Maternity Expense')}
                  onChange={this.handleExpensesSelected('form.whatExpensesAreYouClaimingFor')}
                />
                <span>
                  <FormattedMessage {...messages.maternityExpense} />
                </span>
                {expensesSelected.includes('Maternity Expense') && (
                  <div>
                    <PageSectionHeader className='pt-2 ml-4'>
                      <FormattedMessage {...messages.maternityExpenseDescription} />
                    </PageSectionHeader>
                    <div className={'ml-4 pt-2 row'}>
                      <div className={'col-sm-12 form-group'}>
                        <b className='pt-2'>
                          <FormattedMessage {...messages.maternityExpenseBenefit} />
                          <FNOLTooltip dataFor={'maternityExpense'} content={messages.maternityExpenseToolTip.defaultMessage} />
                        </b>
                        {this.renderExpensesItems('infoMaternityExpenseItems')}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className={'col-sm-12 form-group'}>
                <input
                  disabled={isReadOnly}
                  type="checkbox"
                  name="Oncology"
                  checked={expensesSelected.includes('Oncology')}
                  onChange={this.handleExpensesSelected('form.whatExpensesAreYouClaimingFor')}
                />
                <span>
                  <FormattedMessage {...messages.oncology} />
                </span>
                {expensesSelected.includes('Oncology') && (
                  <div>
                    <PageSectionHeader className='pt-2 ml-4'>
                      <FormattedMessage {...messages.oncologyDescription} />
                    </PageSectionHeader>
                    <div className={'ml-4 pt-2 row'}>
                      <div className={'col-sm-12 form-group'}>
                        <b className='pt-2'>
                          <FormattedMessage {...messages.oncologyExpenseBenefits} />
                          <FNOLTooltip dataFor={'oncologyExpense'} content={messages.oncologyExpenseBenefitsTooltip.defaultMessage} />
                        </b>
                        {this.renderExpensesItems('infoOncologyItems')}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.whatExpensesAreYouClaimingFor}
                />
              </ErrorMessage>
            </Validate>
          </div>
        </div>
        {!isSummary && (
          <WizardNavigationButtons
            onBack={this.props.prev}
            onNext={this.handleNext}
            onSave={this.handleSaveButtonClick}
            canMoveNext
            listLength={1}
            submitted={submitted}
            loading={loading}
            saveButtonId={this.saveButtonId}
            showSpinnerForElement={this.showSpinnerForElement}
            handleKeyDown={this.handleKeyDown}
            intl={intl}
            hasFormChanges={this.props.hasFormChanges}
            nextButtonRef={(ref) => {
              this.nextButton = ref;
            }}
          />
        )}
        <ReactResizeDetector handleHeight onResize={this.onResize} />
      </div>
    );
  }
}

MedicalExpensesTugoStep.propTypes = {
  form: PropTypes.object,
  onFormDataChange: PropTypes.func,
  prev: PropTypes.func,
  next: PropTypes.func,
  onFormDataSave: PropTypes.func,
  loading: PropTypes.bool,
  isSummary: PropTypes.bool,
  submitted: PropTypes.bool,
  adjust: PropTypes.func,
  makeValid: PropTypes.func,
  makeError: PropTypes.func,
  onNext: PropTypes.func,
  step: PropTypes.number,
  currentStep: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isReadOnly: PropTypes.bool,
  errors: PropTypes.any,
  onResetFormCustom: PropTypes.func,
  validateStep: PropTypes.string,
  stepName: PropTypes.string,
  intl: intlShape.isRequired,
  insuredsList: PropTypes.any,
  isClaimForFieldEnabled: PropTypes.bool,
  policyTypeCode: PropTypes.string,
  hasFormChanges: PropTypes.bool,
  setFormHasChanges: PropTypes.func,
};

const mapStateToProps = createStructuredSelector({
  form: makeSelectMedicalExpensesTugoStep(),
  errors: makeSelectMedicalExpensesTugoStepErrors(),
  currentStep: makeSelectCurrentStep(),
  loading: makeSelectProgressBarStatus(),
  insuredsList: makeSelectInsuredsList(),
  isClaimForFieldEnabled: makeSelectIsClaimForFieldEnabled(),
  policyTypeCode: makeSelectPolicyTypeCode(),
  hasFormChanges: makeSelectFormHasChanges(),
});

const mapDispatchToProps = (dispatch) => ({
  onFormDataChange: (propertyName, value) => dispatch(handleFormDataChange(propertyName, value)),
  onFormDataSave: () => dispatch(saveFormData()),
  onNext: (errors) => dispatch(nextSlide(errors)),
  onResetFormCustom: (fields) => dispatch(resetFormFields(fields)),
  setFormHasChanges: (hasFormChanges) => dispatch(setFormHasChanges(hasFormChanges)),
});

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps
);
const withReducer = injectReducer({ key: 'medicalExpensesTugoStep', reducer });

export default compose(
  withReducer,
  withConnect,
  injectIntl,
)(MedicalExpensesTugoStep);



// WEBPACK FOOTER //
// ./app/containers/FastCover/Tugo.MedicalExpenses/index.js